// @flow

import type { Dispatch } from 'redux';
import { parse } from 'query-string';
import get from 'lodash/get';
import { updateSort } from '../sort';
import { getEntries } from '../../../../helpers';
import {
  facetMap, TYPE_RANGE, TYPE_CUSTOM, TYPE_CAMPAIGN,
} from '../../config';
import { getSearch } from '../../../router';
import { hydrateFilters } from './filter';
import type { FilterState } from '../../types';

type UrlFilters = {
  [x: string]: string | Array<string>,
};

export const parseFacetsFromUrlFilters = (urlFilters: UrlFilters): FilterState => (
  getEntries(urlFilters)
    .reduce((acc, [bucket, value]) => {
      // check what type of filter this is
      let filterType = facetMap[bucket];
      // If this filter doesn't exist, we'll assume it's an extra campaign param
      if (!filterType && bucket !== TYPE_CUSTOM) {
        filterType = TYPE_CAMPAIGN;
      }

      let prev;
      let newValue;
      // if this filter is a range filter we need to parse the JSON
      if (filterType === TYPE_RANGE) {
        if (Array.isArray(value)) {
          return acc;
        }

        try {
          newValue = JSON.parse(value);
        } catch (e) {
          return acc;
        }
      } else if (filterType === TYPE_CAMPAIGN) {
        newValue = value;
      } else {
        prev = get(acc, `${filterType}.${bucket}`) || [];
        newValue = prev.concat(value);
      }

      return {
        ...acc,
        [filterType.valueOf()]: {
          ...acc[filterType.valueOf()] || {},
          [bucket.valueOf()]: newValue,
        },
      };
    }, {
      term: {},
      range: {},
      campaign: {},
    })
);

const getFiltersFromUrl = () => (
  dispatch: Dispatch<any>,
  getState: Function,
) => {
  // get the search params from the url
  const search = getSearch(getState());

  // parse the filters from the url
  const urlData = parse(search, { arrayFormat: 'bracket' });

  const { sort, ...urlFilters } = urlData;

  if (sort) {
    try {
      // This is an error in flow-typed/npm/query-string_v6.x.x.js
      // $FlowFixMe
      const sortSettings = JSON.parse(sort);
      dispatch(updateSort(sortSettings.property, sortSettings.direction, sortSettings.type));
    } catch (e) {
      console.error('Could not parse sort settings');
    }
  }

  // convert the url fiters to filters our store understands
  const filters = {
    ...parseFacetsFromUrlFilters(urlFilters),
    custom: urlData[TYPE_CUSTOM],
  };

  // dispatch the new filters to the url
  dispatch(hydrateFilters(filters));
};

export default getFiltersFromUrl;
